import { CHART_BARCHART_OPTIONS, CHART_PIECHART_OPTIONS } from "configApp";
import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import { useAppSelector } from "store/hooks";
import { useSalesNetwork } from "utils/hooks/useSalesNetworks";
import useTranslation from "utils/hooks/useTranslation";

interface ProductModalContentProps {
  data?: any
}

const ProductModalContent2: React.FC<ProductModalContentProps> = ({ data }) => {
  const { t } = useTranslation();
  const [, setWindowSize] = useState<number>(); // NOSONAR
  const projectStore = useAppSelector((state) => state.project);
  const { network } = useSalesNetwork();

  const graphEstimation = network?._parameters?.GRAPH_ESTIMATION;
  const showChartCost = !graphEstimation || graphEstimation.split(',').includes('1');
  const showChartConso = !graphEstimation || graphEstimation.split(',').includes('2');

  const barChartOptions = {
    ...CHART_BARCHART_OPTIONS, ...{
      hAxis: {
        title: t('MONTH'),
        titleTextStyle: { italic: false },
        slantedText: true,
        slantedTextAngle: 90,
      }
    }
  };

  const pieChartOptions = { ...CHART_PIECHART_OPTIONS };

  let totalConsoAndCost = {
    conso: {
      heatingTotal: 0,
      DWHTotal: 0,
      AppTotal: 0,
      RafTotal: 0
    },
    cost: {
      heatingTotal: 0,
      DWHTotal: 0,
      AppTotal: 0,
      RafTotal: 0
    }
  };

  useEffect(() => {
    window.addEventListener("resize", handleResize);

    // Fix a bug on smartphone to adapt first chart to screen size
    setTimeout(() => {
      setWindowSize(window.innerWidth)
    }, 100);
  }, []);

  const handleResize = useCallback(() => {
    // @ts-ignore
    delete barChartOptions.animation;
    setWindowSize(window.innerWidth);
  }, []);

  useEffect(() => {
    return () => {
      window.removeEventListener("resize", handleResize);
    }
  }, [handleResize]);

  const resetTotals = () => {
    totalConsoAndCost = {
      conso: {
        heatingTotal: 0,
        DWHTotal: 0,
        AppTotal: 0,
        RafTotal: 0,
      },
      cost: {
        heatingTotal: 0,
        DWHTotal: 0,
        AppTotal: 0,
        RafTotal: 0,
      }
    }
  };

  const filterZeroOnlySeries = (rows: any[]) => {
    if (!rows || !rows.length) return { rows, keptSeries: [] };

    const header = rows[0];
    const lastHeaderCell = header[header.length - 1];
    const hasAnnotation = typeof lastHeaderCell === 'object' && lastHeaderCell && lastHeaderCell.role === 'annotation';
    const seriesStart = 1;
    const seriesEnd = hasAnnotation ? header.length - 2 : header.length - 1;
    const keptSeries: number[] = [];

    for (let col = seriesStart; col <= seriesEnd; col++) {
      const hasNonZero = rows.slice(1).some((row: any[]) => {
        const value = Number((row[col] === null || row[col] === undefined) ? 0 : row[col]);
        return Number.isFinite(value) && value !== 0;
      });
      if (hasNonZero) keptSeries.push(col);
    }

    if (!keptSeries.length) return { rows, keptSeries: [] };

    const filteredHeader = [
      header[0],
      ...keptSeries.map((col) => header[col]),
      ...(hasAnnotation ? [header[header.length - 1]] : []),
    ];

    const filteredRows = rows.slice(1).map((row: any[]) => [
      row[0],
      ...keptSeries.map((col) => row[col]),
      ...(hasAnnotation ? [row[row.length - 1]] : []),
    ]);

    return { rows: [filteredHeader, ...filteredRows], keptSeries };
  };

  const getChartOptionsWithFilteredColors = (options: any, keptSeries: number[]) => {
    if (!keptSeries.length) return options;
    const sourceColors = Array.isArray(options.colors) ? options.colors : [];
    return {
      ...options,
      colors: keptSeries.map((col) => sourceColors[col - 1]).filter(Boolean),
    };
  };

  const drawChartConso = (conso: any) => {
    const chartData = [
      projectStore.project.simulationCool
        ? [t('MONTH'), t('HEATING'), t('HOT_WATER'), t('EXTRA'), t('RAF'), { role: 'annotation' }]
        : [t('MONTH'), t('HEATING'), t('HOT_WATER'), t('EXTRA'), { role: 'annotation' }]
    ];

    resetTotals();
    conso.forEach((row: any) => {
      totalConsoAndCost.conso.heatingTotal += row.HPHeatingConso;
      totalConsoAndCost.conso.DWHTotal += row.HPDWHConso;
      totalConsoAndCost.conso.AppTotal += row.ConsoAppTot;
      if (projectStore.project.simulationCool) {
        totalConsoAndCost.conso.RafTotal += row.RAFConso;
      }

      totalConsoAndCost.cost.heatingTotal += row.PACChCost;
      totalConsoAndCost.cost.DWHTotal += row.PACDWHCost;
      totalConsoAndCost.cost.AppTotal += row.AppCost;
      if (projectStore.project.simulationCool) {
        totalConsoAndCost.cost.RafTotal += row.RAFCost;
      }

      chartData.push(projectStore.project.simulationCool
        ? [moment().month(row.NoMonth - 1).format("MMM"), row.HPHeatingConso, row.HPDWHConso, row.ConsoAppTot, row.RAFConso, (row.HPHeatingConso + row.HPDWHConso + row.ConsoAppTot + row.RAFConso)]
        : [moment().month(row.NoMonth - 1).format("MMM"), row.HPHeatingConso, row.HPDWHConso, row.ConsoAppTot, (row.HPHeatingConso + row.HPDWHConso + row.ConsoAppTot)]);
    });


    const filtered = filterZeroOnlySeries(chartData);
    const filteredBarChartOptions = getChartOptionsWithFilteredColors(barChartOptions, filtered.keptSeries);

    // @ts-ignore
    const data = new window.google.visualization.arrayToDataTable(filtered.rows);
    // @ts-ignore
    const chart = new window.google.visualization.ColumnChart(document.getElementById('chartConso'));

    // find v-axis max value
    let vAxisMax = null;
    for (let i = 1; i < data.getNumberOfColumns(); i++) {
      const range = data.getColumnRange(i);
      vAxisMax = Math.max(vAxisMax || range.max, range.max);
    }
    // add buffer for annotation
    // @ts-ignore
    vAxisMax *= 1.2;

    chart.draw(data, {
      ...filteredBarChartOptions, ...{
        title: t('BARCHART_CONSO_TITLE'),
        vAxis: {
          title: t('CONSUMED_ENERGY'),
          titleTextStyle: { italic: false },
          viewWindow: {
            max: vAxisMax
          }
        }
      }
    });
  };

  const drawChartRepartitionConso = () => {
    const chartData = [
      [t('MONTH'), t('COST', { currency: network?._parameters.currency })],
      [t('HEATING'), totalConsoAndCost.conso.heatingTotal],
      [t('HOT_WATER'), totalConsoAndCost.conso.DWHTotal],
      [t('EXTRA'), totalConsoAndCost.conso.AppTotal],
    ];

    if (projectStore.project.simulationCool) chartData.push([t('RAF'), totalConsoAndCost.conso.RafTotal]);

    const sourceColors = Array.isArray((pieChartOptions as any).colors) ? (pieChartOptions as any).colors : [];
    const keptIndices: number[] = [];
    const filteredData = [chartData[0]];
    for (let i = 1; i < chartData.length; i++) {
      if (chartData[i][1] !== 0) {
        filteredData.push(chartData[i]);
        keptIndices.push(i - 1);
      }
    }

    const filteredPieChartOptions = {
      ...pieChartOptions,
      colors: keptIndices.map((index) => sourceColors[index]).filter(Boolean),
    };

    // @ts-ignore
    const data = new window.google.visualization.arrayToDataTable(filteredData);
    // @ts-ignore
    const formatter = new window.google.visualization.NumberFormat({
      fractionDigits: 0,
      groupingSymbol: ' ',
      suffix: 'kWh'
    });
    formatter.format(data, 1);

    // @ts-ignore
    const chart = new window.google.visualization.PieChart(document.getElementById('chartRepartitionConso'));
    chart.draw(data, { ...filteredPieChartOptions, ...{ title: t('PIECHART_CONSO_TITLE') } });
  };

  const drawChartCout = (conso: any) => {
    const chartData = [
      projectStore.project.simulationCool
        ? [t('MONTH'), t('HEATING'), t('HOT_WATER'), t('EXTRA'), t('RAF'), { role: 'annotation' }]
        : [t('MONTH'), t('HEATING'), t('HOT_WATER'), t('EXTRA'), { role: 'annotation' }],
    ];

    conso.forEach((row: any) => {
      chartData.push(projectStore.project.simulationCool
        ? [moment().month(row.NoMonth - 1).format("MMM"), row.PACChCost, row.PACDWHCost, row.AppCost, row.RAFCost, (row.PACChCost + row.PACDWHCost + row.AppCost + row.RAFCost)]
        : [moment().month(row.NoMonth - 1).format("MMM"), row.PACChCost, row.PACDWHCost, row.AppCost, (row.PACChCost + row.PACDWHCost + row.AppCost)]);
    });
    const filtered = filterZeroOnlySeries(chartData);
    const filteredBarChartOptions = getChartOptionsWithFilteredColors(barChartOptions, filtered.keptSeries);

    // @ts-ignore
    const data = new window.google.visualization.arrayToDataTable(filtered.rows);
    // @ts-ignore
    const chart = new window.google.visualization.ColumnChart(document.getElementById('chartCout'));

    // find v-axis max value
    let vAxisMax = null;
    for (let i = 1; i < data.getNumberOfColumns(); i++) {
      const range = data.getColumnRange(i);
      vAxisMax = Math.max(vAxisMax || range.max, range.max);
    }
    // add buffer for annotation
    // @ts-ignore
    vAxisMax *= 1.2;

    chart.draw(data, {
      ...filteredBarChartOptions, ...{
        title: t('BARCHART_COST_TITLE', { currency: network?._parameters.currency }),
        vAxis: {
          title: t('COST', { currency: network?._parameters.currency }),
          titleTextStyle: { italic: false },
          viewWindow: {
            max: vAxisMax
          }
        }
      }
    });
  };

  const drawChartRepartitionCout = (conso: any) => {
    // Build cost totals here as well, so the cost pie chart works even when
    // the consumption chart is hidden.
    const costTotals = {
      heatingTotal: 0,
      DWHTotal: 0,
      AppTotal: 0,
      RafTotal: 0,
    };

    if (Array.isArray(conso)) {
      conso.forEach((row: any) => {
        costTotals.heatingTotal += row.PACChCost;
        costTotals.DWHTotal += row.PACDWHCost;
        costTotals.AppTotal += row.AppCost;
        if (projectStore.project.simulationCool) {
          costTotals.RafTotal += row.RAFCost;
        }
      });
    }

    const chartData = [
      [t('MONTH'), 'Consomation'],
      [t('HEATING'), costTotals.heatingTotal],
      [t('HOT_WATER'), costTotals.DWHTotal],
      [t('EXTRA'), costTotals.AppTotal],
    ];

    if (projectStore.project.simulationCool) chartData.push([t('RAF'), costTotals.RafTotal]);

    const sourceColors = Array.isArray((pieChartOptions as any).colors) ? (pieChartOptions as any).colors : [];
    const keptIndices: number[] = [];
    const filteredData = [chartData[0]];
    for (let i = 1; i < chartData.length; i++) {
      if (chartData[i][1] !== 0) {
        filteredData.push(chartData[i]);
        keptIndices.push(i - 1);
      }
    }

    const filteredPieChartOptions = {
      ...pieChartOptions,
      colors: keptIndices.map((index) => sourceColors[index]).filter(Boolean),
    };

    // @ts-ignore
    const data = new window.google.visualization.arrayToDataTable(filteredData);
    // @ts-ignore
    const formatter = new window.google.visualization.NumberFormat({
      fractionDigits: 0,
      groupingSymbol: ' ',
      suffix: network?._parameters.currency
    });
    formatter.format(data, 1);

    // @ts-ignore
    const chart = new window.google.visualization.PieChart(document.getElementById('chartRepartitionCout'));
    chart.draw(data, { ...filteredPieChartOptions, ...{ title: t('PIECHART_COST_TITLE', { currency: network?._parameters.currency }) } });
  };

  // google.charts.load('current', {packages: ['corechart'], language: UIStore.lang}) //NOSONAR
  // google.charts.setOnLoadCallback(() => drawChartConso(product.E3)) //NOSONAR
  // google.charts.setOnLoadCallback(() => drawChartRepartitionConso(product.E3)) //NOSONAR
  // google.charts.setOnLoadCallback(() => drawChartCout(product.E3)) // NOSONAR
  // google.charts.setOnLoadCallback(() => drawChartRepartitionCout(product.E3)) //NOSONAR

  useEffect(() => {
    if (showChartConso) {
      drawChartConso(data.E3) // NOSONAR
      // @ts-ignore
      drawChartRepartitionConso(data.E3) //NOSONAR
    }
    if (showChartCost) {
      drawChartCout(data.E3) // NOSONAR
      // @ts-ignore
      drawChartRepartitionCout(data.E3) //NOSONAR
    }
  }, []);

  return (
    <>
      <group
        //data-width="auto"
        data-gap="30"
        data-text-size="medium"
        data-space-vertical="30"
        data-space-horizontal="50"
        data-direction="column"
        data-justify="start"


      >

        {showChartCost && (
          <group>
            <group data-height="450"
              data-fit="2"
              data-length="forcefit"
              data-name="chart-holder-2"
              data-contain="visible"
            >

              <group data-length="autofit" id="chartCout" />
            </group>

            <group
              data-min-height="400"
              data-max-length="350"
              data-fit="1"
              data-length="forcefit"
              data-align="center"
              data-contain="visible"
              data-name="chart-holder-2"
            >
              <group data-length="autofit" data-height="autofit" id="chartRepartitionCout" />
            </group>
          </group>
        )}


        {showChartConso && showChartCost && <space></space>}
        {showChartConso && (
          <group>
            <group data-height="450"
              data-fit="2"
              data-length="forcefit"
              data-name="chart-holder-2"
              data-contain="visible"
            >
              <group data-length="autofit" id="chartConso" />
            </group>

            <group
              data-min-height="400"
              data-max-length="350"
              data-fit="1"
              data-length="forcefit"
              data-align="center"
              data-name="chart-holder-2"
              data-contain="visible"
            >
              <group data-length="autofit" data-height="autofit" id="chartRepartitionConso" />
            </group>
          </group>
        )}

      </group>
    </>
  )
}

export default ProductModalContent2;